/**
 * Scoring one heard word against one roster word, as syllables rather than as letters.
 *
 * The pipeline per word pair, each step a switchable layer:
 *
 *   heard word  →  English-spelling variants  →  romanisation variants  →  segmentations
 *                                                                              ↓
 *   roster word →  romanisation variants  →  segmentations  →  syllable alignment, best wins
 *
 * Romanisation runs on *both* sides, and per syllable rather than per token. The roster is
 * not a fixed point to normalise towards — a family enrols under the spelling they use, so
 * "Tsienhua" sits on the roster exactly as "Cheung" arrives in a transcript.
 *
 * The alignment is an ordinary edit distance whose substitution cost comes from the confusion
 * matrix instead of being 1 for every mismatch. That is the whole idea: "Zhang" against
 * "Chang" is a substitution of cost 0.05, not of cost 1, so it lands where it belongs —
 * next to an exact match, and nowhere near "Zhong".
 */

import { readings, initialsOf, type Syllable } from './syllables';
import { syllableSimilarity } from './confusion';
import { canonicalSpellings, canonicalTokenForms } from './romanization';
import { pinyinSpellings } from './english';

export interface ScoreLayers {
  syllable: boolean;
  englishSpelling: boolean;
  romanization: boolean;
}

/** Segmentations past this are long-shot readings and cost more than they return. */
const MAX_READINGS = 6;

/**
 * Cost of a syllable present on one side and absent on the other.
 *
 * Below 1 on purpose. A recogniser drops and invents whole syllables — "Xiaoming" comes back
 * as "Shauming" — and treating that as a total mismatch of the word throws away the two
 * syllables it got right.
 */
const INDEL = 0.85;

/** How alike two syllable sequences are, 0–1. */
function alignmentSimilarity(a: readonly Syllable[], b: readonly Syllable[]): number {
  if (a.length === 0 || b.length === 0) return 0;

  let prev = Array.from({ length: b.length + 1 }, (_, i) => i * INDEL);
  for (let i = 1; i <= a.length; i++) {
    const row = [i * INDEL];
    for (let j = 1; j <= b.length; j++) {
      const sub = subCost(a[i - 1], b[j - 1]);
      row[j] = Math.min(prev[j] + INDEL, row[j - 1] + INDEL, prev[j - 1] + sub);
    }
    prev = row;
  }

  return Math.max(0, 1 - prev[b.length] / Math.max(a.length, b.length));
}

/**
 * Cost of reading syllable `a` as syllable `b`.
 *
 * An illegal syllable on either side is not pinyin — an English given name, or a run of
 * letters the segmenter could not read — so the confusion matrix has nothing to say about it
 * and it falls back to being either the same string or not.
 */
function subCost(a: Syllable, b: Syllable): number {
  if (a.raw === b.raw) return 0;
  if (!a.legal || !b.legal) return 1;
  return 1 - syllableSimilarity(a, b);
}

const PAIR_CACHE = new Map<string, number>();
const PAIR_CACHE_LIMIT = 20_000;

/**
 * How alike one heard word and one roster word are as Mandarin, 0–1.
 *
 * Zero for a pair that has nothing to do with each other, and — importantly — zero rather
 * than a small number for two English names that merely both fail to be pinyin. The caller
 * takes the maximum of this and the letter-distance route, so a floor here would drag every
 * unrelated pair upward and flatten the ranking.
 */
export function pinyinTokenSimilarity(spoken: string, actual: string, layers: ScoreLayers): number {
  if (!layers.syllable) return 0;
  if (spoken === '' || actual === '') return 0;

  const cacheKey = `${spoken}|${actual}|${layers.englishSpelling ? 1 : 0}${layers.romanization ? 1 : 0}`;
  const cached = PAIR_CACHE.get(cacheKey);
  if (cached !== undefined) return cached;

  const spokenVariants = variantsOf(spoken, layers);
  const actualForms = rosterFormsOf(actual, layers);
  const actualReadings = actualForms.flatMap(form => readings(form, true)).slice(0, MAX_READINGS * 2);

  let best = 0;
  for (const variant of spokenVariants) {
    // An exact hit after rewriting is the strongest evidence this file can produce: "chow"
    // became "zhou" and the roster says "zhou". Nothing an alignment computes should beat it.
    // Both sides are normalised now, so this also catches the case where the *roster* is the
    // side spelled unusually and the transcript was pinyin all along.
    if (actualForms.includes(variant)) {
      best = 1;
      break;
    }
    for (const spokenReading of readings(variant).slice(0, MAX_READINGS)) {
      for (const actualReading of actualReadings) {
        best = Math.max(best, alignmentSimilarity(spokenReading, actualReading));
        if (best === 1) break;
      }
    }
  }

  if (PAIR_CACHE.size >= PAIR_CACHE_LIMIT) PAIR_CACHE.clear();
  PAIR_CACHE.set(cacheKey, best);
  return best;
}

/** The spellings of `spoken` worth trying, per the enabled layers. */
function variantsOf(spoken: string, layers: ScoreLayers): string[] {
  const spellings = layers.englishSpelling ? pinyinSpellings(spoken) : [spoken];
  if (!layers.romanization) return spellings;
  const out = new Set<string>();
  for (const spelling of spellings) {
    out.add(spelling);
    for (const canonical of canonicalSpellings(spelling)) out.add(canonical);
    // Whole-token first, then the same table applied syllable by syllable, which is the only
    // reading that reaches a variant spelling glued to anything else.
    for (const form of canonicalTokenForms(spelling)) out.add(form);
  }
  return [...out];
}

/**
 * The spellings of a *roster* name worth trying.
 *
 * The romanisation table used to be applied to the transcript only, on the assumption that
 * the roster is already written in the pinyin the table maps onto. That assumption is simply
 * false — families enrol under the spelling they use, so the roster holds "Tsienhua" as
 * readily as a recogniser produces "Cheung", and a table applied to one side of the
 * comparison cannot close a gap that exists on the other.
 *
 * The English-spelling rewrites are deliberately *not* applied here. Those exist to read
 * what a recogniser typed, and a roster name was typed by a person who meant it.
 */
function rosterFormsOf(actual: string, layers: ScoreLayers): string[] {
  if (!layers.romanization) return [actual];
  return [actual, ...canonicalTokenForms(actual)];
}

/**
 * Whether this word can be read as Mandarin at all.
 *
 * The distinction that matters is between "these two names are different" and "this layer
 * has nothing to say about these two names". "Harper" is not pinyin and never will be, so
 * the syllable layer scoring it 0 against "Zhu" is not evidence of a mismatch — it is the
 * layer being asked a question outside its domain, and averaging that 0 in alongside the
 * phonetic score silently buries every English name on the roster. The caller uses this to
 * drop the signal rather than score it.
 */
export function isPinyinWord(word: string): boolean {
  const first = readings(word)[0];
  return first !== undefined && first.length > 0 && first.every(syllable => syllable.legal);
}

/**
 * Whether this word has anything Mandarin about it *once the rewriting layers have run*.
 *
 * The order matters and getting it wrong cost 12 points of top-1. "Teoh" is not pinyin —
 * no segmentation of it is legal — so asking `isPinyinWord` about the raw transcript
 * switches the syllable layer off for the very case the layer exists to catch, because
 * "teoh" is 张 and becomes "zhang" one rewrite later. The question has to be asked of the
 * variants, not of what the recogniser happened to type.
 */
export function couldBePinyin(word: string, layers: ScoreLayers): boolean {
  return variantsOf(word, layers).some(isPinyinWord);
}

/**
 * The initials of a whole name — "lsy" for Li Si Yuan.
 *
 * Weak on its own and deliberately kept as its own signal rather than folded into the
 * syllable score: it survives a transcript so mangled that no syllable aligns, which is the
 * one case where every other layer has already given up.
 */
export function nameInitials(tokens: readonly string[]): string {
  return tokens.map(token => initialsOf(readings(token)[0] ?? [])).join('');
}

/**
 * How much two initial strings agree, 0–1.
 *
 * Order is honoured but not required. A teacher says "Chen Emma" as readily as "Emma Chen",
 * and the roster stores one of those two — scoring the other as a total mismatch threw the
 * signal away on about half of all utterances. An out-of-order match is worth less than an
 * in-order one, which is the most this layer can reasonably claim to know.
 */
export function initialsSimilarity(a: string, b: string): number {
  if (a === '' || b === '') return 0;
  if (a === b) return 1;
  return Math.max(orderedInitials(a, b), 0.8 * orderedInitials(sorted(a), sorted(b)));
}

const sorted = (s: string) => [...s].sort().join('');

/** A subsequence match: the heard initials all appear, in order, in the roster name's. */
function orderedInitials(a: string, b: string): number {
  const short = a.length <= b.length ? a : b;
  const long = a.length <= b.length ? b : a;
  let i = 0;
  for (const ch of long) if (i < short.length && short[i] === ch) i++;
  if (i < short.length) return 0;
  return short.length / long.length;
}
